import React, { useState, useEffect } from "react";
import Breadcrumb from "../../../components/Breadcrumb";
import Grid from "../../../components/Grid";
import AnouncementsService from "../../../Api/AnouncementsService";
import { useNavigate } from "react-router-dom";
import { toast } from 'react-toastify';
import Swal from 'sweetalert2/dist/sweetalert2.js';

const columns = [
    { 
      header: 'Title',
      key: 'title'
    },
    {
      header: 'Type',
      key: 'type',
      render: (item) => (
        <span className={`${item.type === 'public' ? 'bg-success-focus text-success-600' : 'bg-warning-focus text-warning-600'} px-24 py-4 radius-4 fw-medium text-sm`}>
          {item.type.charAt(0).toUpperCase() + item.type.slice(1)}
        </span>
      )
    },
    {
      header: 'Content',
      key: 'content',
      render: (item) => (
        <span>{item.content.length > 50 ? item.content.substring(0, 50) + '...' : item.content}</span>
      )
    },
    {
      header: 'Courses',
      key: 'course_ids',
      render: (item) => (
        <span>{item.course_ids?.length || 0} courses</span>
      )
    }
];

const AnouncementsList = () => {
  const navigate = useNavigate();
  const [announcements, setAnnouncements] = useState([]);
  const [loading, setLoading] = useState(true);
  const announcementAPI = new AnouncementsService();
  const [currentPage, setCurrentPage] = useState(1);
  const [itemsPerPage, setItemsPerPage] = useState(10);

  const fetchAnnouncements = async () => {
    try {
      setLoading(true);
      const response = await announcementAPI.GetAllAnouncements(currentPage, itemsPerPage);
      if (response.data && response.data.data) {
        setAnnouncements(response.data.data);
        toast.success('Announcements loaded successfully!');
      }
    } catch (err) {
      console.error("Error fetching announcements:", err);
      toast.error("Failed to load announcements. Please try again later.");
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    fetchAnnouncements();
  }, [currentPage, itemsPerPage]);

  const handleItemsPerPage = (perPage) => {
    setItemsPerPage(perPage);
    setCurrentPage(1);
  };

  const handleEdit = (announcement) => {
    navigate(`/add-announcement?id=${announcement.id}`);
  };

  const handleView = (announcement) => {
    navigate(`/view-announcement?id=${announcement.id}`);
  };

  const handleDelete = async (announcement) => {
    try {
      const result = await Swal.fire({
        title: 'Are you sure?',
        text: "You won't be able to revert this!",
        icon: 'warning',
        showCancelButton: true,
        confirmButtonColor: '#3085d6',
        cancelButtonColor: '#d33',
        confirmButtonText: 'Yes, delete it!',
        showLoaderOnConfirm: true,
        preConfirm: async () => {
          try {
            const response = await announcementAPI.DeleteById(announcement.id);
            if (response.data && response.data.status === "success") {
              return true;
            } else {
              throw new Error(response.data?.message || 'Failed to delete announcement');
            }
          } catch (error) {
            Swal.showValidationMessage(
              `Delete failed: ${error.response?.data?.message || error.message}`
            );
          }
        },
        allowOutsideClick: () => !Swal.isLoading()
      });

      if (result.isConfirmed) {
        await Swal.fire({
          title: 'Deleted!',
          text: 'Announcement has been deleted.',
          icon: 'success',
          timer: 1500,
          showConfirmButton: false
        });
        toast.success('Announcement deleted successfully!');
        fetchAnnouncements();
      }
    } catch (error) {
      console.error("Error in delete operation:", error);
      toast.error('An unexpected error occurred');
    }
  };

  if (loading) {
    return (
      <div className="text-center p-5">
        <div className="spinner-border text-primary" role="status">
          <span className="visually-hidden">Loading...</span>
        </div>
      </div>
    );
  }

  return (
    <>
      <Breadcrumb title="Announcements List" />
      
      <Grid 
        data={announcements}
        columns={columns}
        add="add-announcement"
        onEdit={handleEdit}
        onDelete={handleDelete}
        onView={handleView}
        onItemsPerPageChange={handleItemsPerPage}
        currentPage={currentPage}
        onPageChange={setCurrentPage}
        itemsPerPage={itemsPerPage}
        showSearch={false}
        showStatusFilter={false}
      />
    </>
  );
};

export default AnouncementsList;
